import { createRESTStore } from "@stores/shared/createRESTStore";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";

// Client-side cache for widget rolodex data (API responses are user-specific, not server-cached):
// 1. Widget mounts → no cache → fetch from API → store result with a timestamp
// 2. Widget needs data again within TTL → return cached data, no API call
// 3. Cache older than TTL → next fetchRolodex call fetches fresh from API
// 4. User clicks refresh → forceRefresh bypasses cache regardless of age
// 5. Per-user isolation: the store resets on page refresh, and useWidgetCacheReset empties it
//    when a session ends without one (session expiry, sign-out in another tab)
const CACHE_TTL = 5 * 60 * 1000;
const PAGE_LENGTH = 3;

const useWidgetRolodexStore = createRESTStore({
  entityName: "widgetRolodex",
  apiPaths: {
    fetch: API.BASE + API.FRONTEND.WIDGETS,
  },
  initialState: {},

  additionalActions: (set, get) => ({
    cache: {},
    fetchingWidgets: {},

    fetchRolodex: async (widgetId, forceRefresh = false) => {
      const entry = get().cache[widgetId];
      if (!forceRefresh && entry && Date.now() - entry.fetchedAt < CACHE_TTL)
        return;

      set((state) => ({
        fetchingWidgets: { ...state.fetchingWidgets, [widgetId]: true },
      }));

      try {
        const page = entry?.page ?? 0;
        const { data: result } = await apiClient.post(
          `${get().apiPaths.fetch}/${widgetId}/rolodex`,
          { page, pageLength: PAGE_LENGTH },
        );

        set((state) => ({
          cache: {
            ...state.cache,
            [widgetId]: {
              items: result.items,
              rolodex: result.rolodex,
              fetchedAt: Date.now(),
              page,
            },
          },
          fetchingWidgets: { ...state.fetchingWidgets, [widgetId]: false },
        }));
      } catch (error) {
        set((state) => ({
          fetchingWidgets: { ...state.fetchingWidgets, [widgetId]: false },
        }));
        console.log(error);
      }
    },

    setPage: async (widgetId, page) => {
      const zeroIndexed = page - 1;
      set((state) => ({
        cache: {
          ...state.cache,
          [widgetId]: {
            ...state.cache[widgetId],
            page: zeroIndexed,
            fetchedAt: 0,
          },
        },
      }));
      await get().fetchRolodex(widgetId, true);
    },
  }),
});

export default useWidgetRolodexStore;
